import React, { useEffect, useRef, useState } from "react";
import { RootRouteProps } from "routers/types/stack";
import { useIsFocused, useRoute } from "@react-navigation/native";
import {
  _goToNote,
  _goToFactor,
  _goToReminder,
  _goToAddCustomer,
  _goToAddNoteForMySelf,
} from "routers/helper";

import {
  View,
  Platform,
  FlatList,
  SafeAreaView,
  TouchableOpacity,
  StatusBar,
} from "react-native";
import {
  Text,
  Icon,
  Input,
  Error,
  Empty,
  Button,
  Header,
  Loading,
  LoadingBox,
  CustomerBox,
  AddButtonFloat,
  AnimatedInfinityList,
} from "components";
import Filters, { CustomersFilterT } from "./Filters";
import SelectedFiltersView from "screens/components/SelectedFiltersView";

import { sizes, colors, strings } from "assets/constants";
import { conditions, liveSearchInput, sn } from "utils";
import { useChmod, useFlag } from "hooks";
import css from "styles";

import {
  useCustomers,
  useCustomerCategories,
  useChangeCustomersGroup,
} from "clients/apis";
import { filtersItems } from "assets/data";
import { useRecoilValue } from "recoil";
import { authAtom } from "stores";
import ChangeGroupModal from "./ChangeGroupModal";

let timer: ReturnType<typeof setTimeout>;

const allCategory: CustomerCategoryT = {
  id: undefined,
  title: strings.all,
  customers_count: 0,
};

const viewOffset = sizes.screenW / 2 - 40;
const categoryItemHeight = 42;
const ListSeparator = () => (
  <View
    style={{
      width: 3,
      height: categoryItemHeight,
      backgroundColor: colors.screensBg,
    }}
  />
);

export default function Customers() {
  const { chmod } = useChmod();

  const { target } = useRoute<RootRouteProps<"customers">>().params;
  const headerTitle = target
    ? strings.pick_customer_for + " " + strings[target]
    : strings.customers;

  const isFocused = useIsFocused();

  const tabs = useRef<FlatList>(null);
  const {
    company: { color },
  } = useRecoilValue(authAtom);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const { flg: typing, on: startTyping, off: stopTyping } = useFlag();
  const { flg: filters, on: showFilters, off: hideFilters } = useFlag();

  const [search, setSearch] = useState("");
  const [qs, setQs] = useState<CustomersFilterT>({});
  const [categoryId, setCategoryId] = useState<number | undefined>(undefined);
  const {
    data: categories,
    error: categoriesError,
    refetch: refetchCategories,
    isLoading: categoriesLoading,
  } = useCustomerCategories();
  const {
    data,
    error,
    isError,
    refetch,
    isLoading,
    hasNextPage,
    isRefetching,
    fetchNextPage,
    isFetchingNextPage,
  } = useCustomers({
    categoryId,
    name: search,
    phone: qs.phone,
    cityId: qs.city?.id,
    status: qs.status?.id,
    birthday: qs?.birthday,
    frindship_id: qs?.frindship?.id,
    passed_cheque_amount: qs?.passed_cheque_amount,
    popular_categories: qs?.popular_category?.id,
  });

  useEffect(() => {
    if (isFocused) {
      refetch();
      refetchCategories();
    }
  }, [isFocused, refetchCategories, refetch]);

  const _onCustomerPress = (customer: CustomerT) => {
    switch (target) {
      case "add_reminder":
        _goToReminder({ customer });
        break;
      case "add_factor":
        _goToFactor({ customer });
        break;
      case "add_note":
        _goToNote({ customer });
        break;

      default:
        break;
    }
  };

  const _onApplyFilters = (newFilters: CustomersFilterT) => {
    hideFilters();
    setQs(newFilters);
  };

  const changeCustomersGroupMut = useChangeCustomersGroup();
  const _onConfirmChangeCustomersGroup = (groupId: number) => {
    hideChangeGroupModal();
    changeCustomersGroupMut?.mutate(
      { group_id: groupId, customer_ids: multiSelectedIds },
      {
        onSuccess: () => {
          setMultiSelectedIds([]);
          refetchCategories();
          refetch();
        },
      }
    );
  };

  const {
    flg: changeGroupModal,
    on: showChangeGroupModal,
    off: hideChangeGroupModal,
  } = useFlag();
  const [multiSelectedIds, setMultiSelectedIds] = useState<Array<number>>([]);

  const _renderItem = (items: []) => {
    if (target) {
      return (
        <View style={{ ...css.wrap, ...css.rRowCenterAlignSpaceBetween }}>
          {items.map((customer: CustomerT, key: number) => (
            <View key={String(key)} style={css.responsiveBoxContainer}>
              <CustomerBox.Simple
                customer={customer}
                onPress={_onCustomerPress}
              />
            </View>
          ))}
        </View>
      );
    }

    return (
      <View style={{ ...css.responsiveListContainer }}>
        {items.map((customer: CustomerT, key: number) => {
          const itemIndex = multiSelectedIds?.indexOf(customer?.id);
          const active = itemIndex > -1;

          const _onLongPress = () => {
            if (multiSelectedIds?.length > 0) {
              return;
            }

            setMultiSelectedIds((prev) => [...prev, customer?.id]);
          };

          const _onPress = () => {
            if (multiSelectedIds?.length > 0) {
              active
                ? setMultiSelectedIds(
                    multiSelectedIds?.filter((id) => id !== customer.id)
                  )
                : setMultiSelectedIds((prev) => [...prev, customer?.id]);
              return;
            }

            key === expandedIndex
              ? setExpandedIndex(null)
              : setExpandedIndex(key);
          };

          return (
            <View key={String(key)} style={css.responsiveBoxContainer}>
              <CustomerBox.Collapsible
                onLongPress={_onLongPress}
                extraData={{ categories }}
                customer={customer}
                expanded={key === expandedIndex}
                onPress={_onPress}
              />
              {multiSelectedIds?.length > 0 && (
                <TouchableOpacity
                  onPress={_onPress}
                  style={{
                    right: -5,
                    top: -5,
                    position: "absolute",
                    ...css.centralElements,
                    width: 20,
                    height: 20,
                    borderWidth: active ? 0 : 1,
                    borderRadius: 7,
                    borderColor: colors.text.normal,
                    backgroundColor: active ? colors.theme : colors.white,
                  }}
                >
                  <Icon
                    size={18}
                    type="antDesign"
                    name={"check"}
                    color={active ? colors.white : colors.text.normal}
                  />
                </TouchableOpacity>
              )}
            </View>
          );
        })}
      </View>
    );
  };

  const _renderContent = () => {
    if (isLoading || typing || categoriesLoading) {
      return <Loading style={{ marginTop: 20 }} />;
    }

    if (isError || categoriesError) {
      return <Error error={error} reset={refetch} loading={isRefetching} />;
    }

    if (data?.pages?.[0]?.data?.data?.length === 0) {
      return (
        <Empty
          icon={{ name: "user" }}
          style={{ marginTop: 70 }}
          message={strings.no_customer}
        />
      );
    }

    return (
      <AnimatedInfinityList
        onRefresh={async () => {
          await Promise.all([refetch(), refetchCategories()]);
        }}
        renderItemList={_renderItem}
        responseData={data}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        paddingBottom={!target ? 70 : 0}
        paddingTop={sizes.space}
      />
    );
  };

  const _renderCategory = (params: {
    item: CustomerCategoryT;
    index: number;
  }) => {
    const { item, index } = params;

    const currentIndex =
      Platform.OS === "web"
        ? [
            {
              ...allCategory,
              customers_count: categories?.data?.total_customers,
            },
            ...categories?.data?.data,
          ]?.length -
          index -
          1
        : index;

    const active = categoryId === item.id;
    const _onSelect = () => {
      setMultiSelectedIds([]);
      tabs.current?.scrollToIndex({
        index: currentIndex,
        animated: true,
        viewOffset,
      });
      setCategoryId(item.id);
    };
    const contentColor = active ? color : colors.text.gray;

    return (
      <TouchableOpacity
        onPress={_onSelect}
        activeOpacity={sizes.tapOpacity}
        style={{
          ...css.centralElements,
          paddingHorizontal: 20,
          height: categoryItemHeight,
          backgroundColor: active ? colors.white : colors.innerItemsBg,
        }}
      >
        <Text
          style={{ top: 4 }}
          size={"sm"}
          weight="bold"
          color={active ? color : colors.textColorGray}
        >
          {item.title}
        </Text>

        <View style={{ ...css.rRowCentral, height: 25, bottom: 4 }}>
          <Icon color={contentColor} name={"user"} size={11} />
          {Number(item.customers_count) > 0 ? (
            <View style={{ marginRight: 5 }}>
              <Text color={contentColor} weight="extraBold" size={"sm"}>
                {item.customers_count}
              </Text>
            </View>
          ) : (
            <View
              style={{
                ...css.absolute,
                width: 1,
                height: 15,
                top: 7,
                backgroundColor: contentColor,
                transform: [{ rotate: "60deg" }],
              }}
            />
          )}
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <>
      <View
        style={{
          justifyContent: "center",
          height: 65 + StatusBar.currentHeight,
        }}
      >
        <Header title={headerTitle} />
        {multiSelectedIds?.length > 0 && (
          <Button
            absolute
            left={70}
            top={15 + StatusBar.currentHeight}
            width={140}
            height={40}
            contentSeparator={4}
            onPress={showChangeGroupModal}
            title={{
              size: "sm",
              value: "تغییر گروه",
              color: colors.theme,
              weight: "extraBold",
            }}
            icon={{ name: "repeat", color: colors.theme, size: 15 }}
            bgColor={colors.theme + "33"}
            style={{ borderRadius: 0 }}
          />
        )}
      </View>
      <>
        <SafeAreaView style={{ ...css.container, ...css.screenBg }}>
          <View
            style={{
              ...css.paddingH,
              ...css.rowCenterAlign,
              marginTop: sizes.space,
            }}
          >
            <Input
              onChangeText={(text) =>
                liveSearchInput(
                  text,
                  search,
                  setSearch,
                  timer,
                  startTyping,
                  stopTyping
                )
              }
              placeholder={strings.search}
              icon={{ name: "search" }}
              hideError
              leftComponent={
                <Button
                  onPress={showFilters}
                  left={4}
                  square={38}
                  radius={sizes.radius.lg}
                  icon={{ name: "sliders", color, size: 24 }}
                  bgColor={colors.white}
                />
              }
            />
          </View>

          {categories?.data?.data && (
            <View
              style={{
                height: categoryItemHeight,
                borderBottomWidth: 1,
                borderColor: colors.border + "55",
                backgroundColor: colors.white,
                marginTop: sizes.space,
              }}
            >
              <FlatList
                keyExtractor={(_, key) => String(key)}
                ref={tabs}
                inverted
                horizontal
                showsHorizontalScrollIndicator={false}
                ItemSeparatorComponent={ListSeparator}
                contentContainerStyle={{
                  ...css.growContainer,
                  ...css.centralElements,
                  height: categoryItemHeight,
                }}
                data={[
                  {
                    ...allCategory,
                    customers_count: categories?.data?.total_customers,
                  },
                  ...categories?.data?.data,
                ]}
                renderItem={_renderCategory}
              />
            </View>
          )}

          {conditions.isFiltered(qs) && (
            <View style={{ paddingTop: sizes.space }}>
              <SelectedFiltersView
                filters={qs}
                onChange={setQs}
                filterItems={filtersItems.customers}
              />
            </View>
          )}

          {_renderContent()}

          <Filters
            visible={filters}
            initials={qs}
            apply={_onApplyFilters}
            hide={hideFilters}
          />
        </SafeAreaView>
        {target === "add_note" && (
          <Button
            onPress={_goToAddNoteForMySelf}
            width={"auto"}
            height={40}
            title={{ value: strings.for_myself }}
            style={{
              margin: 10,
              bottom: 10,
              ...css.absolute,
              ...css.selfStart,
              paddingHorizontal: 10,
            }}
          />
        )}
        {!target && chmod("customer", "POST") && (
          <AddButtonFloat onPress={_goToAddCustomer} />
        )}
      </>

      {changeGroupModal && (
        <ChangeGroupModal
          extraData={{ categories }}
          onConfirm={_onConfirmChangeCustomersGroup}
          onCancel={hideChangeGroupModal}
        />
      )}

      {changeCustomersGroupMut?.isLoading && <LoadingBox />}
    </>
  );
}
